import React, { createContext, useContext, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { emo } from '../components/ui/Ico';

const ToastContext = createContext(null);

let toastId = 0;

const COLORS = {
  success: { bg: '#edf4ee', border: '#6f9a77', icon: '✅', text: '#2f5236' },
  error:   { bg: '#f8ebe9', border: '#e3b3ad', icon: '❌', text: '#a8483f' },
  warning: { bg: '#f8f1e2', border: '#c4933f', icon: '⚠️', text: '#5a3e2b' },
  info:    { bg: '#edf1f6', border: '#b8c7d8', icon: 'ℹ️', text: '#4f6a86' },
};

function ToastItem({ toast, onRemove }) {
  const c = COLORS[toast.type] || COLORS.info;
  return (
    <div
      onClick={() => onRemove(toast.id)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
        background: c.bg, border: `1px solid ${c.border}`,
        color: c.text, padding: '0.75rem 1rem', borderRadius: '10px',
        boxShadow: '0 4px 16px rgba(0,0,0,.12)', cursor: 'pointer',
        maxWidth: '360px', width: '100%',
        animation: 'toastIn 0.25s ease',
        fontSize: '0.9rem', lineHeight: '1.4',
      }}
    >
      <span style={{ flexShrink: 0, fontSize: '1rem' }}>{emo(c.icon)}</span>
      <span style={{ flex: 1 }}>{emo(toast.message)}</span>
    </div>
  );
}

function ToastContainer({ toasts, remove }) {
  if (!toasts.length) return null;
  return ReactDOM.createPortal(
    <>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div style={{
        position: 'fixed', top: '1.25rem', right: '1.25rem',
        zIndex: 99999, display: 'flex', flexDirection: 'column', gap: '0.5rem',
        pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'all' }}>
            <ToastItem toast={t} onRemove={remove} />
          </div>
        ))}
      </div>
    </>,
    document.body
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info', duration = 3500) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => remove(id), duration);
  }, [remove]);

  const showSuccess = useCallback((msg, dur) => toast(msg, 'success', dur), [toast]);
  const showError   = useCallback((msg, dur) => toast(msg, 'error',   dur || 5000), [toast]);
  const showWarning = useCallback((msg, dur) => toast(msg, 'warning', dur), [toast]);
  const showInfo    = useCallback((msg, dur) => toast(msg, 'info',    dur), [toast]);

  return (
    <ToastContext.Provider value={{ toast, showSuccess, showError, showWarning, showInfo }}>
      {children}
      <ToastContainer toasts={toasts} remove={remove} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
